import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SystemHealthPanel } from '@/components/dashboard/SystemHealthPanel';
import { useSystemHealth } from '@/hooks/useSystemHealth';
import { 
  Shield, 
  Activity, 
  Settings, 
  CheckCircle,
  AlertTriangle,
  Info,
  TrendingUp
} from 'lucide-react';

export default function SystemHealthDashboard() {
  const { systemHealth, isLoading, refreshAllMetrics } = useSystemHealth();
  const [activeTab, setActiveTab] = useState('overview');

  const getOverallHealthColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getHealthStatus = (score: number) => {
    if (score >= 90) return { text: 'ممتاز', color: 'bg-green-500' };
    if (score >= 70) return { text: 'جيد', color: 'bg-yellow-500' };
    return { text: 'يحتاج تحسين', color: 'bg-red-500' };
  };

  const overallScore = Math.round(
    (systemHealth.security.securityScore + systemHealth.performance.indexEfficiency) / 2
  );

  const healthStatus = getHealthStatus(overallScore);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* العنوان الرئيسي */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">مراقبة صحة النظام</h1>
          <p className="text-muted-foreground">
            مراقبة شاملة لأداء وأمان النظام مع التكامل الذكي
          </p>
        </div>
        <Badge 
          variant="secondary" 
          className={`${healthStatus.color} text-white px-4 py-2 text-sm`}
        >
          الحالة العامة: {healthStatus.text}
        </Badge>
      </div>

      {/* نظرة عامة سريعة */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className={`h-8 w-8 ${getOverallHealthColor(systemHealth.security.securityScore)}`} />
              <div>
                <p className="text-2xl font-bold">{systemHealth.security.securityScore}%</p>
                <p className="text-sm text-muted-foreground">الأمان</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{systemHealth.performance.indexEfficiency}%</p>
                <p className="text-sm text-muted-foreground">الأداء</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Settings className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{systemHealth.stats.employees + systemHealth.stats.clients}</p>
                <p className="text-sm text-muted-foreground">إجمالي المستخدمين</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{systemHealth.stats.bookings}</p>
                <p className="text-sm text-muted-foreground">الحجوزات النشطة</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* التبويبات الرئيسية */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            نظرة عامة
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            الأمان
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* تفاصيل الصحة العامة */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                الصحة العامة للنظام
              </CardTitle>
              <CardDescription>
                تقييم شامل لجميع مكونات النظام
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span>النتيجة الإجمالية</span>
                <span className={`font-bold ${getOverallHealthColor(overallScore)}`}>
                  {overallScore}%
                </span>
              </div>
              <Progress value={overallScore} className="h-3" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-sm">الأنظمة المتصلة: 4/4</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <span className="text-sm">التحذيرات: {systemHealth.security.warnings}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-500" />
                  <span className="text-sm">آخر تحديث: {new Date(systemHealth.stats.lastUpdated).toLocaleString('ar')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <SystemHealthPanel />
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                حالة الأمان المحسنة
              </CardTitle>
              <CardDescription>
                تم تحسين الأمان بشكل كبير - انخفضت المشاكل الحرجة من 81 إلى {systemHealth.security.totalIssues}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{systemHealth.security.securityScore}%</p>
                  <p className="text-sm text-green-600">درجة الأمان</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{systemHealth.security.criticalIssues}</p>
                  <p className="text-sm text-red-600">مشاكل حرجة</p>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{systemHealth.security.warnings}</p>
                  <p className="text-sm text-yellow-600">تحذيرات</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{systemHealth.security.tablesWithoutRLS}</p>
                  <p className="text-sm text-blue-600">جداول بحاجة RLS</p>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ التحسينات المطبقة:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• تم تفعيل Row Level Security على جميع الجداول الحساسة</li>
                  <li>• إضافة فهارس لتحسين الأداء</li>
                  <li>• تحسين الدوال الأمنية مع search_path</li>
                  <li>• إنشاء نظام مراقبة محاولات الوصول غير المصرح بها</li>
                  <li>• إضافة جدول تتبع حالة الأنظمة الخارجية</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}